import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { healthApi } from '@/api/health';
import { ApiError } from '@/api/client';
import type { HealthMetric } from '@/types/Health';

interface UseHealthResult {
  metrics: HealthMetric[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  save: (data: HealthMetric) => Promise<{ ok: boolean; message?: string }>;
  remove: (date: string) => Promise<{ ok: boolean; message?: string }>;
}

export const healthKey = ['health-metrics'] as const;

export function useHealth(enabled: boolean): UseHealthResult {
  const qc = useQueryClient();

  const query = useQuery<HealthMetric[], Error>({
    queryKey: healthKey,
    queryFn: () => healthApi.history().then((list) => list ?? []),
    enabled,
  });

  const saveMut = useMutation({
    mutationFn: (data: HealthMetric) => healthApi.upsert(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: healthKey });
    },
  });

  const removeMut = useMutation({
    mutationFn: (date: string) => healthApi.remove(date),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: healthKey });
    },
  });

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: healthKey });
  }, [qc]);

  const save = useCallback(
    async (data: HealthMetric) => {
      try {
        await saveMut.mutateAsync(data);
        return { ok: true };
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to save metrics';
        return { ok: false, message: msg };
      }
    },
    [saveMut],
  );

  const remove = useCallback(
    async (date: string) => {
      try {
        await removeMut.mutateAsync(date);
        return { ok: true };
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to delete metrics';
        return { ok: false, message: msg };
      }
    },
    [removeMut],
  );

  const errIsAuth =
    query.error instanceof ApiError && (query.error.status === 401 || query.error.status === 403);
  const error = query.error && !errIsAuth ? query.error.message : null;

  return {
    metrics: query.data ?? [],
    loading: query.isPending && enabled,
    error,
    refresh,
    save,
    remove,
  };
}
